/**
 * Centralized Validation Schemas
 *
 * This file contains all validation schemas used across the application.
 * All form validations should be defined here for consistency and reusability.
 */

import * as Yup from 'yup';
import { FormikErrors } from 'formik';
import type { UserRoleType } from '../utils/roleUtils';

/**
 * ============================================================================
 * FACILITY VALIDATION SCHEMAS
 * ============================================================================
 */

/**
 * Validation Schema Factory for Facility forms (Add Facility page).
 * Rules live in `./facilityFormValidation` (user story).
 */
export { getAddFacilityValidationSchema as getFacilityValidationSchema } from './facilityFormValidation';

/**
 * ============================================================================
 * USER VALIDATION SCHEMAS
 * ============================================================================
 */

/** Add/Edit User (AddUser page) — Radiologist & Technician rules live in `./userFormYup`. */
export { getUserValidationSchema } from './userFormYup';

/**
 * ============================================================================
 * PROFILE VALIDATION SCHEMAS
 * ============================================================================
 */

/**
 * Get profile validation schema based on user role
 *
 * ViewProfile only lets users edit **Full Name** and **Phone** in Personal Details.
 * Other role fields stay in Formik for submit payloads but must not gate Save / `isValid`,
 * or radiologists (and technicians) with incomplete shift/modality data in the API
 * would never pass validation while editing name/phone.
 *
 * Previous per-role validation (kept below, commented) required fields that ViewProfile
 * does not expose for editing, which kept Save disabled for Radiologist / Technician etc.
 */
export const getProfileValidationSchema = (_userRole: UserRoleType) => {
  /*
  const baseSchema = Yup.object().shape({
    fullName: Yup.string().required('Full Name is required'),
    gender: Yup.string().required('Gender is required'),
    email: Yup.string().required('Email is required').email('Invalid email address'),
    phoneNo: Yup.string()
      .required('Phone No. is required')
      .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
    username: Yup.string().required('Username is required'),
  });

  if (userRole === 'SuperAdmin') {
    return baseSchema.shape({
      dateOfBirth: Yup.string().required('Date of Birth is required'),
    });
  }

  if (userRole === 'Admin') {
    return baseSchema.shape({
      employeeId: Yup.string().required('Employee ID is required'),
    });
  }

  // For Technician and Radiologist, use full validation
  if (userRole === 'Technician') {
    return baseSchema.shape({
      employeeId: Yup.string().required('Employee ID is required'),
      dateOfBirth: Yup.string().required('Date of Birth is required'),
      shiftStart: Yup.string().required('Shift start time is required'),
      shiftEnd: Yup.string().required('Shift end time is required'),
    });
  }

  if (userRole === 'Radiologist') {
    return baseSchema.shape({
      dateOfBirth: Yup.string().required('Date of Birth is required'),
      registrationNo: Yup.string().required('Registration No. is required'),
      qualification: Yup.string().required('Qualification is required'),
      reportingModalityAccess: Yup.array()
        .of(Yup.string())
        .min(1, 'At least one modality is required')
        .required('Reporting Modality Access is required'),
      shiftStart: Yup.string().required('Shift start time is required'),
      shiftEnd: Yup.string().required('Shift end time is required'),
    });
  }

  return baseSchema;
  */

  const phoneNo = Yup.string()
    .required('Mobile number is required')
    .test('mobile-digits', 'Mobile number must be exactly 10 digits', value => {
      const digits = String(value ?? '').replace(/\D/g, '');
      if (digits.length === 0) {
        return false;
      }
      if (digits.length > 10) {
        return false;
      }
      return /^\d{10}$/.test(digits);
    });

  return Yup.object().shape({
    fullName: Yup.string().trim().required('Full Name is required'),
    phoneNo,
  });
};

/**
 * ============================================================================
 * PASSWORD VALIDATION HELPERS
 * ============================================================================
 */

/**
 * Password validation message
 */
const PASSWORD_REQUIREMENTS_MESSAGE =
  'Password must be at least 8 characters with at least one special character, one capital letter, one small letter, and one number';

/**
 * Password validation regex patterns
 */
const hasUpperCase = /[A-Z]/;
const hasLowerCase = /[a-z]/;
const hasNumber = /[0-9]/;
const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

/**
 * Validate password strength
 */
const validatePassword = (password: string): string | undefined => {
  if (!password) {
    return 'Password is required';
  }

  if (password.length < 8) {
    return PASSWORD_REQUIREMENTS_MESSAGE;
  }

  if (!hasUpperCase.test(password)) {
    return PASSWORD_REQUIREMENTS_MESSAGE;
  }

  if (!hasLowerCase.test(password)) {
    return PASSWORD_REQUIREMENTS_MESSAGE;
  }

  if (!hasNumber.test(password)) {
    return PASSWORD_REQUIREMENTS_MESSAGE;
  }

  if (!hasSpecialChar.test(password)) {
    return PASSWORD_REQUIREMENTS_MESSAGE;
  }

  return undefined;
};

/**
 * ============================================================================
 * CHANGE PASSWORD VALIDATION
 * ============================================================================
 */

/**
 * Interface for change password form values
 */
export interface ChangePasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

/**
 * Validation function for change password form
 */
export const validateChangePassword = (
  values: ChangePasswordFormValues
): FormikErrors<ChangePasswordFormValues> => {
  const errors: FormikErrors<ChangePasswordFormValues> = {};

  if (!values.newPassword) {
    errors.newPassword = 'New password is required';
  } else {
    const passwordError = validatePassword(values.newPassword);
    if (passwordError) {
      errors.newPassword = passwordError;
    }
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password';
  } else if (values.newPassword !== values.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  return errors;
};
